// ─────────────────────────────────────────────
// KLOTHIQ — Bulk Import Column Definitions
// Single source of truth for template generation AND upload
// validation, so the two can never drift apart (spec FEAT-BULK-
// IMPORT-001 §6 — the whole reason this lives in its own file).
// ─────────────────────────────────────────────

import type { Attribute } from '@/hooks/useMasterConfig';

export interface ImportColumn {
  key: string; // internal field key, e.g. 'name', 'sku', 'attr:TEAM'
  label: string; // header text shown in the sheet
  level: 'product' | 'variant';
  kind: 'fixed' | 'attribute';
  required: boolean;
  attributeCode?: string; // kind === 'attribute' only
  /** 'teams' = validated against the org's real `teams` table by name.
   *  'fixed' = validated against attr.options (a static enum list). */
  optionsSource?: 'teams' | 'fixed';
  fixedOptions?: string[];
  inputType?: string;
}

const FIXED_PRODUCT_COLUMNS: ImportColumn[] = [
  { key: 'product_code', label: 'Product Code', level: 'product', kind: 'fixed', required: false },
  { key: 'name', label: 'Product Name', level: 'product', kind: 'fixed', required: true },
  {
    key: 'category',
    label: 'Subcategory / collection',
    level: 'product',
    kind: 'fixed',
    required: false,
  },
  {
    key: 'target_audience',
    label: 'Target Audience',
    level: 'product',
    kind: 'fixed',
    required: false,
  },
  { key: 'age_group', label: 'Age Group', level: 'product', kind: 'fixed', required: false },
  { key: 'description', label: 'Description', level: 'product', kind: 'fixed', required: false },
];

const FIXED_VARIANT_COLUMNS: ImportColumn[] = [
  { key: 'size', label: 'Size', level: 'variant', kind: 'fixed', required: true },
  { key: 'color', label: 'Color', level: 'variant', kind: 'fixed', required: false },
  { key: 'sku', label: 'SKU', level: 'variant', kind: 'fixed', required: false },
  { key: 'default_cost', label: 'Cost Price', level: 'variant', kind: 'fixed', required: true },
  { key: 'default_price', label: 'Sell Price', level: 'variant', kind: 'fixed', required: true },
  {
    key: 'opening_stock',
    label: 'Opening Stock',
    level: 'variant',
    kind: 'fixed',
    required: false,
  },
  {
    key: 'low_stock_level',
    label: 'Low Stock Alert',
    level: 'variant',
    kind: 'fixed',
    required: false,
  },
];

// Size/Color are always the two fixed variant columns above — never emit a
// second, duplicate column even if the product type also maps a SIZE/COLOR
// attribute (the default seed data maps both to every apparel type).
const SKIP_ATTRIBUTE_CODES = new Set(['SIZE', 'COLOR']);

function attrToColumn(
  attr: Attribute & { is_required?: boolean; catalog_field?: boolean },
  level: 'product' | 'variant',
): ImportColumn {
  const code = (attr.code || attr.name.toUpperCase().replace(/[^A-Z0-9]/g, '_')) as string;
  const isTeam =
    !attr.catalog_field && (code === 'TEAM' || attr.name.trim().toLowerCase() === 'team');
  const isFixedSelect = !isTeam && attr.input_type === 'select' && Array.isArray(attr.options);

  return {
    key: `attr:${code}`,
    label: attr.name,
    level,
    kind: 'attribute',
    required: !!attr.is_required,
    attributeCode: code,
    optionsSource: isTeam ? 'teams' : isFixedSelect ? 'fixed' : undefined,
    fixedOptions: isFixedSelect ? (attr.options as unknown as string[]) : undefined,
    inputType: attr.input_type,
  };
}

/** Builds the ordered column list for a given product type's dynamic attributes. */
export function buildImportColumns(
  dynamicAttributes: Array<Attribute & { is_required?: boolean }>,
): ImportColumn[] {
  const attrs = dynamicAttributes ?? [];
  const productAttrs = attrs
    .filter((a) => !a.is_variant_level && !SKIP_ATTRIBUTE_CODES.has((a.code || '').toUpperCase()))
    .map((a) => attrToColumn(a, 'product'));
  const variantAttrs = attrs
    .filter((a) => a.is_variant_level && !SKIP_ATTRIBUTE_CODES.has((a.code || '').toUpperCase()))
    .map((a) => attrToColumn(a, 'variant'));

  return [...FIXED_PRODUCT_COLUMNS, ...productAttrs, ...FIXED_VARIANT_COLUMNS, ...variantAttrs];
}

export function columnHeaderLabel(col: ImportColumn): string {
  return col.required ? `${col.label} *` : col.label;
}

/** Strips the required-marker and normalizes for case/whitespace-insensitive header matching. */
export function normalizeHeaderLabel(label: string): string {
  return label
    .trim()
    .replace(/\s*\*$/, '')
    .trim()
    .toLowerCase();
}

export function sheetNameForType(typeCode: string): string {
  const clean = (typeCode || 'PRODUCT').toUpperCase().replace(/[^A-Z0-9_]/g, '_');
  return `${clean}_IMPORT`.slice(0, 31);
}
